import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiAlertTriangle as AlertTriangle, FiXCircle as XCircle, FiCheckCircle as CheckCircle, FiFilter as Filter, FiActivity as Activity, FiClock as Clock, FiTrash2 as TrashIcon, FiSquare as Square, FiCheckSquare as CheckSquare, FiFileText as FileText } from 'react-icons/fi';
import { getIncidents, getIncidentStats, acknowledgeIncident, resolveIncident, bulkDeleteIncidents, bulkAssignRunProject, bulkAssignRunWorkload } from '../../../../services/aiOperationsApi';
import { useProject } from '../../../../contexts/ProjectContext';
import { useFilter } from '../../../../contexts/FilterContext';
import ProjectAssignmentSelect from '../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../components/WorkloadAssignmentSelect';
import { useWorkloads } from '../../../../features/hub/workloads/hooks/useWorkloads';
import SafetyPage from './SafetyPage';

interface Incident {
  id: string;
  severity: string;
  incident_type: string;
  status: string;
  error_message?: string;
  detected_at?: string;
  resolved_at?: string;
  resolution_notes?: string;
  run_id?: string;
  project_id?: string;
  workload_id?: string;
  [key: string]: unknown;
}

interface IncidentStats {
  total_incidents: number;
  by_status: Record<string, number>;
  avg_resolution_time_hours: number;
  [key: string]: unknown;
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (updater: (p: number) => number) => void;
}

const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) => {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-2 mt-6 pb-6">
      <button
        onClick={() => onPageChange(p => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300
                 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed
                 transition-colors"
      >
        Previous
      </button>
      <span className="text-gray-700 dark:text-gray-300">
        Page {currentPage} of {totalPages}
      </span>
      <button
        onClick={() => onPageChange(p => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300
                 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed
                 transition-colors"
      >
        Next
      </button>
    </div>
  );
};

const IncidentsContent = () => {
  const { selectedProjectId, projects } = useProject();
  const { workloads } = useWorkloads();
  const { filterType, selectedId: filterId } = useFilter();
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [stats, setStats] = useState<IncidentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    severity: '',
    status: '',
    incidentType: '',
    days: 7
  });
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => prev.size === incidents.length ? new Set() : new Set(incidents.map((i) => i.id)));
  }, [incidents]);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!globalThis.confirm(`Are you sure you want to delete ${selectedIds.size} incident(s)?`)) return;
    setActionLoading(true);
    try {
      await bulkDeleteIncidents(Array.from(selectedIds));
      setSelectedIds(new Set());
      await loadData();
    } catch (err: unknown) {
      console.error('Error deleting incidents:', err);
      alert('Failed to delete incidents: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setActionLoading(false);
    }
  };

  const getProjectName = (projectId?: string) => (
    projectId ? (projects.find((project) => project.id === projectId)?.name ?? null) : null
  );

  const getWorkloadName = (workloadId?: string) => (
    workloadId ? (workloads.find((w) => w.id === workloadId)?.name ?? null) : null
  );

  const handleBulkAssignProject = async (projectId: string | null) => {
    const runIds = Array.from(
      new Set(
        incidents
          .filter((incident) => selectedIds.has(incident.id))
          .map((incident) => incident.run_id)
          .filter((runId): runId is string => Boolean(runId))
      )
    );

    if (runIds.length === 0) return;

    await bulkAssignRunProject(runIds, projectId);
    setIncidents((prev) => prev.map((incident) => (
      selectedIds.has(incident.id) ? { ...incident, project_id: projectId ?? undefined } : incident
    )));
    setSelectedIds(new Set());
  };

  const handleBulkAssignWorkload = async (workloadId: string | null) => {
    const runIds = Array.from(
      new Set(
        incidents
          .filter((incident) => selectedIds.has(incident.id))
          .map((incident) => incident.run_id)
          .filter((runId): runId is string => Boolean(runId))
      )
    );

    if (runIds.length === 0) return;

    await bulkAssignRunWorkload(runIds, workloadId);
    setSelectedIds(new Set());
    loadData();
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters, selectedProjectId, filterType, filterId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load incidents - include days filter to match stats
      const incidentsParams = {
        page: currentPage,
        limit: 50,
        days: filters.days,
        ...Object.fromEntries(
          Object.entries(filters).filter(([k, v]) => v !== '' && k !== 'days')
        )
      };

      const incidentsData = await getIncidents(incidentsParams);
      setIncidents(incidentsData.data || incidentsData.incidents || []);
      setTotalPages(incidentsData.meta?.totalPages || 1);

      // Load stats
      const statsParams = { days: filters.days };
      const statsData = await getIncidentStats(statsParams);
      setStats(statsData);
    } catch (err: unknown) {
      console.error('Error loading incidents data:', err);
      setError((err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (incidentId: string) => {
    setActionLoading(true);
    try {
      await acknowledgeIncident(incidentId);
      await loadData(); // Reload data
    } catch (err: unknown) {
      console.error('Error acknowledging incident:', err);
      alert('Failed to acknowledge incident: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolveClick = (incident: Incident) => {
    setSelectedIncident(incident);
    setResolutionNotes('');
    setShowResolveModal(true);
  };

  const handleResolveSubmit = async () => {
    if (!resolutionNotes.trim()) {
      alert('Please provide resolution notes');
      return;
    }

    setActionLoading(true);
    try {
      await resolveIncident(selectedIncident!.id, resolutionNotes);
      setShowResolveModal(false);
      setSelectedIncident(null);
      setResolutionNotes('');
      await loadData(); // Reload data
    } catch (err: unknown) {
      console.error('Error resolving incident:', err);
      alert('Failed to resolve incident: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setActionLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatNumber = (num: number | null | undefined) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  const getSeverityColor = (severity: string | null | undefined) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'low':
        return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getSeverityIcon = (severity: string | null | undefined) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high':
        return <XCircle className="w-4 h-4" />;
      case 'medium':
        return <AlertTriangle className="w-4 h-4" />;
      case 'low':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string | null | undefined) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
      case 'investigating':
        return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
      case 'resolved':
        return 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
      case 'ignored':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const handleRowClick = (incident: Incident) => {
    navigate(`/ai-operations/incidents/${incident.id}`);
  };


  if (loading && !stats) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-300">Error loading incidents data: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
          Agent Incidents
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
          Monitor and manage incidents across agent executions
        </p>
      </div>

      {/* Summary Cards */}
      {stats && (
        <div className="mb-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Incidents</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatNumber(stats.total_incidents || 0)}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                  {formatNumber(stats.by_status?.active || 0)}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Resolved</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {formatNumber(stats.by_status?.resolved || 0)}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Avg Resolution Time</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {stats.avg_resolution_time_hours ? `${stats.avg_resolution_time_hours.toFixed(1)}h` : 'N/A'}
                </p>
              </div>
              <Clock className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 transition-colors">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={toggleSelectAll}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
            title={selectedIds.size === incidents.length && incidents.length > 0 ? 'Deselect all' : 'Select all'}
          >
            {selectedIds.size === incidents.length && incidents.length > 0 ? (
              <CheckSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            ) : (
              <Square className="w-5 h-5" />
            )}
          </button>
          <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Filters</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="severity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Severity
            </label>
            <select id="severity"
              value={filters.severity}
              onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select id="status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="ignored">Ignored</option>
            </select>
          </div>

          <div>
            <label htmlFor="incident-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Incident Type
            </label>
            <select id="incident-type"
              value={filters.incidentType}
              onChange={(e) => setFilters({ ...filters, incidentType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="timeout">Timeout</option>
              <option value="system_error">System Error</option>
              <option value="model_error">Model Error</option>
              <option value="tool_failure">Tool Failure</option>
            </select>
          </div>

          <div>
            <label htmlFor="time-range" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Time Range
            </label>
            <select id="time-range"
              value={filters.days}
              onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="mb-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center justify-between transition-colors">
          <span className="text-sm text-blue-800 dark:text-blue-300">
            {selectedIds.size} incident{selectedIds.size === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <ProjectAssignmentSelect
              bulk
              currentProjectId={null}
              onAssign={handleBulkAssignProject}
              size="sm"
            />
            <WorkloadAssignmentSelect
              bulk
              onAssign={handleBulkAssignWorkload}
              size="sm"
            />
            <button
              onClick={handleBulkDelete}
              disabled={actionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Incidents Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm transition-colors overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Incidents ({incidents.length})
          </h2>
        </div>

        {incidents.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              No incidents found
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              All agent executions are running smoothly
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-10">
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Severity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Error Message
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Detected At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {incidents.map((incident, index) => (
                    <tr
                      key={incident.id || index}
                      onClick={() => handleRowClick(incident)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRowClick(incident); }} role="row" tabIndex={0}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                    >
                      <td className="px-3 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation">
                        <button
                          onClick={() => toggleSelect(incident.id)}
                          className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          {selectedIds.has(incident.id) ? (
                            <CheckSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(incident.severity)}`}>
                          {getSeverityIcon(incident.severity)}
                          {incident.severity || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 dark:text-gray-300">
                          {incident.incident_type || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(incident.status)}`}>
                          {incident.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-gray-900 dark:text-gray-300 max-w-md truncate">
                            {incident.error_message || 'No message available'}
                          </p>
                          {getProjectName(incident.project_id) && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded">
                              {getProjectName(incident.project_id)}
                            </span>
                          )}
                          {getWorkloadName(incident.workload_id) && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded">
                              {getWorkloadName(incident.workload_id)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                        {formatTimestamp(incident.detected_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-3">
                          <span className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300">
                            View &gt;
                          </span>
                          {incident.run_id && (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigate(`/ai-operations/traces/${incident.run_id}`); }}
                              className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                              title="View Trace Details"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              Trace
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation">
                        {incident.status === 'active' && (
                          <button
                            onClick={() => handleAcknowledge(incident.id)}
                            disabled={actionLoading}
                            className="text-yellow-600 hover:text-yellow-700 mr-3 disabled:opacity-50"
                          >
                            Acknowledge
                          </button>
                        )}
                        {(['active', 'investigating'].includes(incident.status)) && (
                          <button
                            onClick={() => handleResolveClick(incident)}
                            disabled={actionLoading}
                            className="text-green-600 hover:text-green-700 disabled:opacity-50"
                          >
                            Resolve
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </>
        )}
      </div>

      {/* Resolve Modal */}
      {showResolveModal && selectedIncident && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Resolve Incident
            </h3>
            <div className="mb-4">
              <label htmlFor="resolution-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Resolution Notes
              </label>
              <textarea id="resolution-notes"
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe how this incident was resolved..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowResolveModal(false);
                  setResolutionNotes('');
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300
                         hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResolveSubmit}
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded-md
                         hover:bg-green-700 disabled:opacity-50"
              >
                {actionLoading ? 'Resolving...' : 'Resolve Incident'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const IncidentsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get('section') || 'safety';

  const SectionTab = ({ id, label }: { id: string; label: string }) => (
    <button
      onClick={() => setSearchParams({ section: id }, { replace: true })}
      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
        section === id
          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
      }`}
    >
      {label}
    </button>
  );

  return (
    <>
      {/* Section tabs */}
      <div className="mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <SectionTab id="safety" label="Safety" />
            <SectionTab id="incidents" label="Incidents" />
          </nav>
        </div>
      </div>
      {section === 'safety' ? <SafetyPage /> : <IncidentsContent />}
    </>
  );
};

export default IncidentsPage;
